import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { comboLabel } from "@/domain/modifiers/labels";
import type { Binding } from "@/domain/types";
import { ComboKbds } from "@/render/components/ComboKbds";
import { fitInlineCount } from "./fit-strip";

interface OtherLayer {
  /** Canonical combo this binding lives on; "" is the base (no-modifier) layer. */
  combo: string;
  binding: Binding;
}

interface ReadoutStripProps {
  /** The selected key's legend; repeated as the strip's accent anchor so each pill reads as a formula. */
  legend: string;
  /** The same key's bindings on other modifier holds, pre-sorted by modifier weight. */
  others: OtherLayer[];
  /** Click a pill to latch that layer's modifiers and read out its binding. */
  onChip?: (combo: string) => void;
}

/** Matches the `.ro-others` flex gap in app.css; fed to the fit math. */
const GAP = 8;

// useLayoutEffect trims before paint (no flash) in the browser, but warns under server render.
// On the server (no window) fall back to useEffect, which is a no-op during renderToString.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** One pill body: the combo as keycap(s), then the command label. Base ("") shows a "Base" keycap. */
function pillBody(o: OtherLayer) {
  return (
    <>
      {o.combo ? (
        <ComboKbds combo={o.combo} className="ro-kc" />
      ) : (
        <span className="ro-kc">Base</span>
      )}
      <span className="ro-pill-cmd">{o.binding.name}</span>
    </>
  );
}

/**
 * The footer modifier strip: an accent anchor (the selected key + "+") followed by one pill per other
 * modifier layer, each reading as a formula (the key held with its modifier(s) names a command). The
 * strip is the only part of the footer that yields: it measures its own width and drops the tail into
 * a "+N more" popover rather than wrapping or overrunning the (non-shrinking) selected binding.
 */
export function ReadoutStrip({ legend, others, onChip }: ReadoutStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  // pre-measure default: show everything, so SSR / happy-dom (clientWidth 0) stay fully populated
  const [inlineCount, setInlineCount] = useState(others.length);
  const [open, setOpen] = useState(false);

  // measure how many pills fit from the hidden ruler's child widths; recompute on resize and
  // whenever the readout changes. A row with NO width at all (no layout: SSR, happy-dom) keeps
  // every pill inline until a real browser width arrives; a laid-out row squeezed to nothing
  // collapses to anchor + chip instead (fitInlineCount), so the tail stays reachable.
  useIsoLayoutEffect(() => {
    const container = containerRef.current;
    const ruler = rulerRef.current;
    const row = container?.parentElement; // the .readout flex row
    if (!container || !ruler || !row) return;
    const measure = () => {
      if (row.clientWidth === 0) {
        setInlineCount(others.length);
        return;
      }
      const kids = Array.from(ruler.children) as HTMLElement[];
      const anchorEl = kids[0];
      const moreEl = kids[kids.length - 1];
      if (!anchorEl || !moreEl || kids.length < 2) return; // anchor + sample-chip bracket the pills
      // available width for the strip = the row's inner width minus its (non-shrinking) left blocks
      // and the row gaps. Measuring the row rather than the strip's own width avoids the
      // shrink-to-content feedback loop a self-measured flex item would hit.
      const cs = getComputedStyle(row);
      const rowGap = parseFloat(cs.columnGap) || 0;
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const sibs = Array.from(row.children) as HTMLElement[];
      let used = 0;
      for (const c of sibs) if (c !== container) used += c.offsetWidth;
      const avail = row.clientWidth - padX - used - rowGap * Math.max(0, sibs.length - 1);
      const anchorW = anchorEl.offsetWidth;
      const moreW = moreEl.offsetWidth;
      const pillWidths = kids.slice(1, -1).map((el) => el.offsetWidth);
      setInlineCount(fitInlineCount({ avail, anchorW, pillWidths, moreW, gap: GAP }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [others, legend]);

  // a new selection closes the overflow popover (others is stable between presses, so this does
  // not fire on unrelated parent re-renders)
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on each new readout, not on open changes
  useEffect(() => setOpen(false), [others, legend]);

  // while open, close on Escape or a pointer outside the chip + its popover
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // the readout's own window keydown listener would otherwise read Escape out as a key press
      // ("Unbound"); capture-phase + stop keeps Esc a pure popover dismissal
      e.stopImmediatePropagation();
      e.preventDefault();
    };
    const onDown = (e: PointerEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey, true); // capture: run before the readout listener
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  if (others.length === 0) return null;

  const inline = others.slice(0, inlineCount);
  const overflow = others.slice(inlineCount);
  // a resize that re-fits every pill unmounts the "+N more" chip; derive `open` closed with it so
  // the Escape listener releases and a later re-narrow does not reopen the popover unbidden
  // (in-render reset, the same pattern as the page's hoverKey)
  if (overflow.length === 0 && open) setOpen(false);
  const anchor = (
    <span className="ro-anchor">
      <span className="ro-anchor-key">{legend}</span>
      <span className="ro-plus">+</span>
    </span>
  );
  const pillButton = (o: OtherLayer, role?: "menuitem") => (
    <button
      type="button"
      key={o.combo || "base"}
      role={role}
      className="ro-pill-box ro-other"
      title={`${comboLabel(o.combo)} layer`}
      onClick={() => {
        onChip?.(o.combo);
        if (role === "menuitem") setOpen(false); // a popover pick also dismisses the popover
      }}
    >
      {pillBody(o)}
    </button>
  );

  return (
    <div className="ro-others" ref={containerRef}>
      {anchor}
      <div className="ro-pill-run">{inline.map((o) => pillButton(o))}</div>
      {overflow.length > 0 ? (
        <div className="ro-more-wrap" ref={moreRef}>
          <button
            type="button"
            className="ro-more"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            +{overflow.length} more
          </button>
          {open ? (
            <div className="ro-overflow" role="menu">
              {overflow.map((o) => pillButton(o, "menuitem"))}
            </div>
          ) : null}
        </div>
      ) : null}
      {/* hidden ruler: anchor + every pill + a sample chip, measured to size the inline run. Its pills
          carry .ro-pill-box only (not .ro-other), so test/behavior queries never match them while the
          shared box model keeps their widths identical to the real pills. */}
      <div className="ro-ruler" aria-hidden="true" ref={rulerRef}>
        {anchor}
        {others.map((o) => (
          <span className="ro-pill-box" key={o.combo || "base"}>
            {pillBody(o)}
          </span>
        ))}
        <span className="ro-more">+{others.length} more</span>
      </div>
    </div>
  );
}
